/**
/**
 * `PilotPicker` — the desktop-OS-style **profile picker** that sits between slot login and the
 * shell. A logged-in player lands here and chooses which of their owned pilots they play, or
 * starts a new one.
 *
 * It's a **full surface with its own chrome** — like `/login`, it is NOT inside the shell (a player
 * hasn't "entered" yet; picking a pilot is what does that). It renders on the shared
 * {@link GmsScreen} backdrop as a brutalist
 * window, visually consistent with the `/login` terminal: a white laminate card with the GMS window
 * chrome, a brand band, then a wrapping grid of pilot tiles. One tile per owned pilot (a hex avatar
 * holding the callsign's initial — no `portrait` field exists on `PilotData` yet — plus the
 * callsign), then a trailing ＋ "New pilot" tile that mints a fresh pilot and lands on its PILOT ·
 * EDIT page with FORGE's panel open (the guided build advisor), via the caller's `onNewPilot`.
 *
 * The picker is the single always-on landing: a zero-pilot player sees just the ＋ tile. Both
 * selecting an existing pilot (`onSelect` → `session.selectPilot`) and minting a new one
 * (`onNewPilot`) are view + roster concepts handled by the caller; this component is pure
 * presentation over the roster snapshot.
 */
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { Icon } from "../../lib/Icon";
import type { PilotData } from "@lancer-vtt/domain";
import { avatarGlyph } from "./avatarGlyph";
import { AvatarImage } from "./PilotAvatar";
import { GmsScreen } from "./GmsScreen";
import "./gms-pilots.css";

export function PilotPicker({
  pilots,
  onSelect,
  onNewPilot,
}: {
  pilots: PilotData[];
  onSelect: (pilotId: string) => void;
  /** Mint a fresh pilot and land on PILOT · EDIT with FORGE open (the picker's "＋ New pilot" tile). */
  onNewPilot: () => void;
}) {
  return (
    <GmsScreen>
      <div className="gms-pilots-stage">
        <div className="gms-pilots-window">
          <div className="gms-pilots-mat" />
          <div className="gms-card">
            {/* title bar — matches the desktop + login window chrome */}
            <div className="gms-titlebar">
              <span style={{ letterSpacing: 0 }}>◆</span>
              <span className="gms-titlebar-name">PILOT REGISTRY</span>
              <div className="gms-titlebar-fill" />
              <span className="gms-pilots-status">ROSTER</span>
            </div>

            {/* brand band */}
            <div className="gms-pilots-brand">
              <span className="gms-pilots-hex" aria-hidden="true">
                ⬡
              </span>
              <h1 className="gms-pilots-title">SELECT PILOT</h1>
              <span className="gms-pilots-sub">OMNINET ROSTER</span>
              <p className="gms-pilots-hint">Choose who drives this Omnihook.</p>
            </div>

            {/* the pilot tiles */}
            <div className="gms-pilots-grid">
              {pilots.map((pilot) => (
                <button
                  key={pilot.id}
                  type="button"
                  className="gms-pilots-tile"
                  onClick={() => onSelect(pilot.id)}
                  aria-label={`Select pilot ${pilot.callsign}`}
                >
                  <span className="gms-pilots-avatar" aria-hidden="true">
                    {pilot.image ? (
                      <AvatarImage
                        src={pilot.image}
                        alt=""
                        className="gms-pilots-avatar-img"
                        fallback={avatarGlyph(pilot.callsign)}
                      />
                    ) : (
                      avatarGlyph(pilot.callsign)
                    )}
                  </span>
                  <span className="gms-pilots-callsign">{pilot.callsign}</span>
                </button>
              ))}

              <button
                type="button"
                className="gms-pilots-tile gms-pilots-new"
                onClick={onNewPilot}
                aria-label="Create a new pilot"
              >
                <span className="gms-pilots-avatar" aria-hidden="true">
                  <Icon icon={faPlus} />
                </span>
                <span className="gms-pilots-callsign">New pilot</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </GmsScreen>
  );
}
